import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import { Box, BoxProps } from "@mui/material";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import { useCustomNavigation } from "hooks/useCustomNavigation";
import { useRoute } from "hooks/useRoute";
import queryString from "query-string";
import * as React from "react";
import { FC, useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { get, set } from "lodash";
import {useTranslate} from "react-admin";

type SearchInputProps = {
  placeholder?: string;
  queryPropertyName?: string;
} & BoxProps;

export const Search: FC<SearchInputProps> = (props) => {
  const [value, setValue] = React.useState("");
  
  const t = useTranslate();

  const { navigate } = useCustomNavigation();

  const location = useLocation();

  const propertyName = props.queryPropertyName ?? "search";

  const { isAvailable: isFilterAvailable, navigate: navigateToFilter } =
    useRoute({ path: "./filter" });

  const parsed = useMemo(
    () => queryString.parse(location.search),
    [location.search]
  );

  const submit = React.useCallback(() => {
    const fieldValue = value !== "" ? value : undefined;

    const newValue = set(parsed, propertyName, fieldValue);

    navigate({
      pathname: ".",
      search: `?${queryString.stringify(newValue)}`,
    });
  }, [navigate, parsed, propertyName, value]);

  useEffect(() => {
    const value = get(parsed, propertyName);

    if (typeof value === "string") {
      setValue(value);
    }
  }, [parsed, propertyName]);

  return (
    <Box {...props}>
      <Box
        component="div"
        sx={{
          p: "2px 4px",
          display: "flex",
          alignItems: "center",
          width: "100%",
          boxSizing: "border-box",
          color: "text.secondary",
          backgroundColor: "rgba(129, 129, 129, 0.1)",
          borderRadius: "10px",
        }}
      >
        <IconButton type="button" sx={{ p: "10px" }} aria-label="search">
          <SearchIcon />
        </IconButton>
        <InputBase
          sx={{ ml: 1, flex: 1 }}
          placeholder={props.placeholder ?? t("ra.action.search")}
          inputProps={{ "aria-label": "search google maps" }}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyUp={(event) => {
            if (event.key === "Enter") {
              submit();
              event.stopPropagation();
            }
          }}
        />
        {isFilterAvailable ? (
          <>
            <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
            <IconButton
              color="primary"
              sx={{ p: "10px" }}
              aria-label="directions"
              onClick={navigateToFilter}
            >
              <TuneIcon />
            </IconButton>
          </>
        ) : null}
      </Box>
    </Box>
  );
};
